import { useControlledState } from '@react-stately/utils';
import { createContext, useMemo, useState } from 'react';

import { useLiveRef } from '@gitbook/util-react-hooks';

/**
 * Represents the current state of autocomplete.
 */
type AutoCompleteState = {
    /**
     * Value used to filter results.
     */
    filterValue: string;
    /**
     * Value of the input.
     */
    value: string;
    /**
     * Set the value of the input.
     */
    setValue: (value: string) => void;
    /**
     * True when the user has started to type something.
     */
    isTouched: boolean;
    /**
     * The ref of the MenuSearch input.
     */
    menuSearchInputRef: (element: HTMLInputElement | null) => void;
    /**
     * Menu search element.
     */
    menuSearchElement: HTMLInputElement | null;
    /**
     * The ref of the element used as input.
     */
    inputRef: React.RefObject<HTMLElement>;
};

/**
 * Autocomplete state context.
 */
export const AutoCompleteStateContext = createContext<AutoCompleteState | null>(null);

export type AutoCompleteProps = {
    children: React.ReactNode;
    /**
     * External input ref to be used as input if the menu has no search input.
     */
    inputRef?: React.RefObject<HTMLElement>;
    /**
     * Default value set in input.
     */
    defaultValue?: string | undefined;
    /**
     * Value used for filtering.
     * By default it's the same as the value.
     */
    filterValue?: string | undefined;
    /**
     * Controlled value of the input.
     */
    value?: string | undefined;
    /**
     * Callback when the value changes.
     */
    onValueChange?: (value: string) => void;
};

/**
 * Autocomplete wrapper that injects the autocomplete state.
 */
export function AutoComplete(props: AutoCompleteProps) {
    const {
        children,
        defaultValue = '',
        filterValue: filterValueProp,
        value: valueProp,
        onValueChange,
        inputRef: inputRefProp,
    } = props;
    const [menuSearchElement, setMenuSearchElement] = useState<HTMLInputElement | null>(null);
    const menuSearchElementRef = useLiveRef(menuSearchElement);
    const inputRef = useLiveRef<HTMLElement | null>(
        menuSearchElementRef.current ?? inputRefProp?.current ?? null
    );
    const [value, setValue] = useControlledState(valueProp, defaultValue, onValueChange);
    const filterValue = filterValueProp ?? value;
    const [isTouched, setIsTouched] = useState(Boolean(value));
    const state = useMemo<AutoCompleteState>(
        () => ({
            inputRef,
            menuSearchInputRef: setMenuSearchElement,
            menuSearchElement,
            filterValue,
            value,
            isTouched,
            setValue: (value: string) => {
                setValue(value);
                setIsTouched(true);
            },
        }),
        [inputRef, value, filterValue, isTouched, setValue, menuSearchElement]
    );
    return (
        <AutoCompleteStateContext.Provider value={state}>
            {children}
        </AutoCompleteStateContext.Provider>
    );
}
